/**
 * 增量序列化器
 *
 * 提供高性能的增量序列化支持，只序列化变更的数据
 * 适用于网络同步、大场景存档、时间回溯等场景
 */
import { Entity } from '../Entity';
import { ComponentSerializer } from './ComponentSerializer';
import { BinarySerializer } from '../../Utils/BinarySerializer';
/**
 * 变更操作类型
 */
export var ChangeOperation;
(function (ChangeOperation) {
    /** 添加新实体 */
    ChangeOperation["EntityAdded"] = "entity_added";
    /** 删除实体 */
    ChangeOperation["EntityRemoved"] = "entity_removed";
    /** 实体属性更新 */
    ChangeOperation["EntityUpdated"] = "entity_updated";
    /** 添加组件 */
    ChangeOperation["ComponentAdded"] = "component_added";
    /** 删除组件 */
    ChangeOperation["ComponentRemoved"] = "component_removed";
    /** 组件数据更新 */
    ChangeOperation["ComponentUpdated"] = "component_updated";
    /** 场景数据更新 */
    ChangeOperation["SceneDataUpdated"] = "scene_data_updated";
})(ChangeOperation || (ChangeOperation = {}));
/**
 * 增量序列化器类
 */
export class IncrementalSerializer {
    /**
     * 创建场景快照
     *
     * @param scene 要快照的场景
     * @param options 序列化选项
     * @returns 场景快照对象
     */
    static createSnapshot(scene, options) {
        const opts = {
            deepComponentComparison: true,
            trackSceneData: true,
            compressSnapshot: false,
            ...options
        };
        const snapshot = {
            version: ++this.snapshotVersion,
            entityIds: new Set(),
            entities: new Map(),
            components: new Map(),
            sceneData: new Map()
        };
        // 快照所有实体
        for (const entity of scene.entities.buffer) {
            snapshot.entityIds.add(entity.id);
            // 存储实体基本信息
            snapshot.entities.set(entity.id, {
                name: entity.name,
                tag: entity.tag,
                active: entity.active,
                enabled: entity.enabled,
                updateOrder: entity.updateOrder,
                ...(entity.parent && { parentId: entity.parent.id })
            });
            // 快照组件
            if (opts.deepComponentComparison) {
                const componentMap = new Map();
                for (const component of entity.components) {
                    const serialized = ComponentSerializer.serialize(component);
                    if (serialized) {
                        // 使用JSON字符串存储，便于后续对比
                        componentMap.set(serialized.type, JSON.stringify(serialized.data));
                    }
                }
                if (componentMap.size > 0) {
                    snapshot.components.set(entity.id, componentMap);
                }
            }
        }
        // 快照场景数据
        if (opts.trackSceneData) {
            for (const [key, value] of scene.sceneData) {
                snapshot.sceneData.set(key, JSON.stringify(value));
            }
        }
        return snapshot;
    }
    /**
     * 计算增量变更
     *
     * @param scene 当前场景
     * @param baseSnapshot 基础快照
     * @param options 序列化选项
     * @returns 增量快照
     */
    static computeIncremental(scene, baseSnapshot, options) {
        const opts = {
            deepComponentComparison: true,
            trackSceneData: true,
            ...options
        };
        const incremental = {
            version: ++this.snapshotVersion,
            timestamp: Date.now(),
            sceneName: scene.name,
            baseVersion: baseSnapshot.version,
            entityChanges: [],
            componentChanges: [],
            sceneDataChanges: []
        };
        const currentEntityIds = new Set();
        // 检测实体变更
        for (const entity of scene.entities.buffer) {
            currentEntityIds.add(entity.id);
            if (!baseSnapshot.entityIds.has(entity.id)) {
                // 新增实体
                incremental.entityChanges.push({
                    operation: ChangeOperation.EntityAdded,
                    entityId: entity.id,
                    entityName: entity.name,
                    entityData: {
                        id: entity.id,
                        name: entity.name,
                        tag: entity.tag,
                        active: entity.active,
                        enabled: entity.enabled,
                        updateOrder: entity.updateOrder,
                        ...(entity.parent && { parentId: entity.parent.id }),
                        components: [],
                        children: []
                    }
                });
                // 新增实体的所有组件都是新增
                for (const component of entity.components) {
                    const serialized = ComponentSerializer.serialize(component);
                    if (serialized) {
                        incremental.componentChanges.push({
                            operation: ChangeOperation.ComponentAdded,
                            entityId: entity.id,
                            componentType: serialized.type,
                            componentData: serialized
                        });
                    }
                }
            }
            else {
                // 检查实体属性变更
                const oldData = baseSnapshot.entities.get(entity.id);
                const entityChanged = oldData.name !== entity.name ||
                    oldData.tag !== entity.tag ||
                    oldData.active !== entity.active ||
                    oldData.enabled !== entity.enabled ||
                    oldData.updateOrder !== entity.updateOrder ||
                    oldData.parentId !== entity.parent?.id;
                if (entityChanged) {
                    incremental.entityChanges.push({
                        operation: ChangeOperation.EntityUpdated,
                        entityId: entity.id,
                        entityData: {
                            name: entity.name,
                            tag: entity.tag,
                            active: entity.active,
                            enabled: entity.enabled,
                            updateOrder: entity.updateOrder,
                            ...(entity.parent && { parentId: entity.parent.id })
                        }
                    });
                }
                // 检查组件变更
                if (opts.deepComponentComparison) {
                    this.detectComponentChanges(entity, baseSnapshot, incremental.componentChanges);
                }
            }
        }
        // 检测删除的实体
        for (const oldEntityId of baseSnapshot.entityIds) {
            if (!currentEntityIds.has(oldEntityId)) {
                incremental.entityChanges.push({
                    operation: ChangeOperation.EntityRemoved,
                    entityId: oldEntityId
                });
            }
        }
        // 检测场景数据变更
        if (opts.trackSceneData) {
            this.detectSceneDataChanges(scene, baseSnapshot, incremental.sceneDataChanges);
        }
        return incremental;
    }
    /**
     * 检测组件变更
     */
    static detectComponentChanges(entity, baseSnapshot, componentChanges) {
        const oldComponents = baseSnapshot.components.get(entity.id);
        const currentComponents = new Map();
        // 收集当前组件
        for (const component of entity.components) {
            const serialized = ComponentSerializer.serialize(component);
            if (serialized) {
                currentComponents.set(serialized.type, serialized);
            }
        }
        // 检测新增和更新的组件
        for (const [type, serialized] of currentComponents) {
            const currentData = JSON.stringify(serialized.data);
            if (!oldComponents || !oldComponents.has(type)) {
                // 新增组件
                componentChanges.push({
                    operation: ChangeOperation.ComponentAdded,
                    entityId: entity.id,
                    componentType: type,
                    componentData: serialized
                });
            }
            else if (oldComponents.get(type) !== currentData) {
                // 组件数据变更
                componentChanges.push({
                    operation: ChangeOperation.ComponentUpdated,
                    entityId: entity.id,
                    componentType: type,
                    componentData: serialized
                });
            }
        }
        // 检测删除的组件
        if (oldComponents) {
            for (const oldType of oldComponents.keys()) {
                if (!currentComponents.has(oldType)) {
                    componentChanges.push({
                        operation: ChangeOperation.ComponentRemoved,
                        entityId: entity.id,
                        componentType: oldType
                    });
                }
            }
        }
    }
    /**
     * 检测场景数据变更
     */
    static detectSceneDataChanges(scene, baseSnapshot, sceneDataChanges) {
        const currentKeys = new Set();
        // 检测新增和更新的场景数据
        for (const [key, value] of scene.sceneData) {
            currentKeys.add(key);
            const currentValue = JSON.stringify(value);
            const oldValue = baseSnapshot.sceneData.get(key);
            if (!oldValue || oldValue !== currentValue) {
                sceneDataChanges.push({
                    operation: ChangeOperation.SceneDataUpdated,
                    key,
                    value
                });
            }
        }
        // 检测删除的场景数据
        for (const oldKey of baseSnapshot.sceneData.keys()) {
            if (!currentKeys.has(oldKey)) {
                sceneDataChanges.push({
                    operation: ChangeOperation.SceneDataUpdated,
                    key: oldKey,
                    value: undefined,
                    deleted: true
                });
            }
        }
    }
    /**
     * 应用增量变更到场景
     *
     * @param scene 目标场景
     * @param incremental 增量快照
     * @param componentRegistry 组件类型注册表
     */
    static applyIncremental(scene, incremental, componentRegistry) {
        // 应用实体变更
        for (const change of incremental.entityChanges) {
            switch (change.operation) {
                case ChangeOperation.EntityAdded:
                    this.applyEntityAdded(scene, change);
                    break;
                case ChangeOperation.EntityRemoved:
                    this.applyEntityRemoved(scene, change);
                    break;
                case ChangeOperation.EntityUpdated:
                    this.applyEntityUpdated(scene, change);
                    break;
            }
        }
        // 应用组件变更
        for (const change of incremental.componentChanges) {
            switch (change.operation) {
                case ChangeOperation.ComponentAdded:
                    this.applyComponentAdded(scene, change, componentRegistry);
                    break;
                case ChangeOperation.ComponentRemoved:
                    this.applyComponentRemoved(scene, change, componentRegistry);
                    break;
                case ChangeOperation.ComponentUpdated:
                    this.applyComponentUpdated(scene, change, componentRegistry);
                    break;
            }
        }
        // 应用场景数据变更
        for (const change of incremental.sceneDataChanges) {
            if (change.deleted) {
                scene.sceneData.delete(change.key);
            }
            else {
                scene.sceneData.set(change.key, change.value);
            }
        }
    }
    static applyEntityAdded(scene, change) {
        if (!change.entityData)
            return;
        const entity = new Entity(change.entityName || 'Entity', change.entityId);
        entity.tag = change.entityData.tag || 0;
        entity.active = change.entityData.active ?? true;
        entity.enabled = change.entityData.enabled ?? true;
        entity.updateOrder = change.entityData.updateOrder || 0;
        scene.addEntity(entity);
    }
    static applyEntityRemoved(scene, change) {
        const entity = scene.entities.findEntityById(change.entityId);
        if (entity) {
            entity.destroy();
        }
    }
    static applyEntityUpdated(scene, change) {
        if (!change.entityData)
            return;
        const entity = scene.entities.findEntityById(change.entityId);
        if (!entity)
            return;
        if (change.entityData.name !== undefined)
            entity.name = change.entityData.name;
        if (change.entityData.tag !== undefined)
            entity.tag = change.entityData.tag;
        if (change.entityData.active !== undefined)
            entity.active = change.entityData.active;
        if (change.entityData.enabled !== undefined)
            entity.enabled = change.entityData.enabled;
        if (change.entityData.updateOrder !== undefined)
            entity.updateOrder = change.entityData.updateOrder;
        if (change.entityData.parentId !== undefined) {
            const newParent = scene.entities.findEntityById(change.entityData.parentId);
            if (newParent && entity.parent !== newParent) {
                if (entity.parent) {
                    entity.parent.removeChild(entity);
                }
                newParent.addChild(entity);
            }
        }
        else if (entity.parent) {
            entity.parent.removeChild(entity);
        }
    }
    static applyComponentAdded(scene, change, componentRegistry) {
        if (!change.componentData)
            return;
        const entity = scene.entities.findEntityById(change.entityId);
        if (!entity)
            return;
        const component = ComponentSerializer.deserialize(change.componentData, componentRegistry);
        if (component) {
            entity.addComponent(component);
        }
    }
    static applyComponentRemoved(scene, change, componentRegistry) {
        const entity = scene.entities.findEntityById(change.entityId);
        if (!entity)
            return;
        const componentClass = componentRegistry.get(change.componentType);
        if (!componentClass)
            return;
        entity.removeComponentByType(componentClass);
    }
    static applyComponentUpdated(scene, change, componentRegistry) {
        if (!change.componentData)
            return;
        const entity = scene.entities.findEntityById(change.entityId);
        if (!entity)
            return;
        const componentClass = componentRegistry.get(change.componentType);
        if (!componentClass)
            return;
        entity.removeComponentByType(componentClass);
        const component = ComponentSerializer.deserialize(change.componentData, componentRegistry);
        if (component) {
            entity.addComponent(component);
        }
    }
    /**
     * 序列化增量快照
     *
     * @param incremental 增量快照
     * @param options 序列化选项
     * @returns 序列化后的数据（JSON字符串或二进制Uint8Array）
     *
     * @example
     * ```typescript
     * // JSON格式（默认）
     * const jsonData = IncrementalSerializer.serializeIncremental(snapshot);
     *
     * // 二进制格式
     * const binaryData = IncrementalSerializer.serializeIncremental(snapshot, {
     *     format: 'binary'
     * });
     *
     * // 美化JSON
     * const prettyJson = IncrementalSerializer.serializeIncremental(snapshot, {
     *     format: 'json',
     *     pretty: true
     * });
     * ```
     */
    static serializeIncremental(incremental, options) {
        const opts = {
            format: 'json',
            pretty: false,
            ...options
        };
        if (opts.format === 'binary') {
            return BinarySerializer.encode(incremental);
        }
        else {
            return opts.pretty
                ? JSON.stringify(incremental, null, 2)
                : JSON.stringify(incremental);
        }
    }
    /**
     * 反序列化增量快照
     *
     * @param data 序列化的数据（JSON字符串或二进制Uint8Array）
     * @returns 增量快照
     *
     * @example
     * ```typescript
     * // 从JSON反序列化
     * const snapshot = IncrementalSerializer.deserializeIncremental(jsonString);
     *
     * // 从二进制反序列化
     * const snapshot = IncrementalSerializer.deserializeIncremental(buffer);
     * ```
     */
    static deserializeIncremental(data) {
        if (typeof data === 'string') {
            return JSON.parse(data);
        }
        else {
            return BinarySerializer.decode(data);
        }
    }
    /**
     * 获取增量快照的统计信息
     *
     * @param incremental 增量快照
     * @returns 统计信息
     */
    static getIncrementalStats(incremental) {
        return {
            totalChanges: incremental.entityChanges.length +
                incremental.componentChanges.length +
                incremental.sceneDataChanges.length,
            entityChanges: incremental.entityChanges.length,
            componentChanges: incremental.componentChanges.length,
            sceneDataChanges: incremental.sceneDataChanges.length,
            addedEntities: incremental.entityChanges.filter((c) => c.operation === ChangeOperation.EntityAdded).length,
            removedEntities: incremental.entityChanges.filter((c) => c.operation === ChangeOperation.EntityRemoved).length,
            updatedEntities: incremental.entityChanges.filter((c) => c.operation === ChangeOperation.EntityUpdated).length,
            addedComponents: incremental.componentChanges.filter((c) => c.operation === ChangeOperation.ComponentAdded).length,
            removedComponents: incremental.componentChanges.filter((c) => c.operation === ChangeOperation.ComponentRemoved).length,
            updatedComponents: incremental.componentChanges.filter((c) => c.operation === ChangeOperation.ComponentUpdated).length
        };
    }
    /**
     * 重置快照版本号（用于测试）
     */
    static resetVersion() {
        this.snapshotVersion = 0;
    }
}
/** 当前快照版本号 */
IncrementalSerializer.snapshotVersion = 0;
//# sourceMappingURL=IncrementalSerializer.js.map